import { ref, readonly } from 'vue'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const installPrompt = ref<BeforeInstallPromptEvent | null>(null)
const isInstallable = ref(false)

function setLifetimeCookie(name: string, value: string) {
  const expires = new Date()
  expires.setFullYear(expires.getFullYear() + 10)
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
}


function isOnRcpTerminal() {
  return window.location.pathname.startsWith('/rcp/terminal')
}

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  installPrompt.value = e as BeforeInstallPromptEvent
  if (isOnRcpTerminal() && !window.matchMedia('(display-mode: standalone)').matches) {
    isInstallable.value = true
  }
})

window.addEventListener('appinstalled', () => {
  isInstallable.value = false
  installPrompt.value = null
  setLifetimeCookie('rcpPWAInstalled', 'true')
})

// Already running as installed PWA — no need to show banner
if (window.matchMedia('(display-mode: standalone)').matches) {
  isInstallable.value = false
}

export function usePWAInstall() {
  // Event may have fired on an admin page before terminal was visited — enable banner now
  if (installPrompt.value && isOnRcpTerminal() && !window.matchMedia('(display-mode: standalone)').matches) {
    isInstallable.value = true
  }

  async function install(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!installPrompt.value) {
      // dev mode: just dismiss
      isInstallable.value = false
      return 'unavailable'
    }
    await installPrompt.value.prompt()
    const { outcome } = await installPrompt.value.userChoice
    if (outcome === 'accepted') {
      isInstallable.value = false
      installPrompt.value = null
      setLifetimeCookie('rcpPWAInstalled', 'true')
    }
    return outcome
  }

  return {
    isInstallable: readonly(isInstallable),
    install,
  }
}
